import useAnalytics, { eventName } from '@/hooks/use-analytics';
import { styled } from '@mui/material';
import Button from '@mui/material/Button';
import type { ButtonProps } from '@mui/material/Button';

export const GenericButton = ({
  component,
  label,
  id,
  type = 'button',
  onClick,
  href,
  fullWidth = true,
  variant = 'contained',
  size = 'large',
  startIcon,
  endIcon,
  color,
  form,
  fireAnalytics = true,
  loading = false,
}: {
  component?: 'button' | 'a';
  fullWidth?: boolean;
  label: string;
  id: string;
  onClick?: () => void;
  href?: string;
  size?: 'small' | 'medium' | 'large';
  type?: 'submit' | 'reset' | 'button';
  variant?: 'text' | 'outlined' | 'contained' | 'chip';
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  color?: 'inherit' | 'primary' | 'secondary' | 'tertiary' | 'success' | 'error' | 'info' | 'warning' | 'text';
  form?: string;
  fireAnalytics?: boolean;
  loading?: boolean;
}) => {
  const { trackClick } = useAnalytics();
  const isChip = variant === 'chip';
  const muiVariant = isChip ? 'outlined' : variant;

  return (
    <StyledButton
      onClick={() => {
        if (fireAnalytics) trackClick(eventName(id));
        if (onClick) onClick();
      }}
      type={type}
      href={href}
      fullWidth={fullWidth}
      variant={muiVariant}
      role='button'
      size={size}
      startIcon={loading ? null : startIcon}
      endIcon={loading ? null : endIcon}
      color={color}
      form={form}
      component={component}
      loading={loading}
      data-chip={isChip ? 'true' : undefined}
    >
      {loading ? '' : label}
    </StyledButton>
  );
};

const StyledButton = styled(Button)<ButtonProps>(({ color }) => {
  const validColors = ['primary', 'secondary', 'tertiary', 'success', 'error', 'info', 'warning'];
  const safeColor = validColors.includes(color as string) ? color : 'secondary';

  const outlinedBorderColor = {
    '&.MuiButton-outlined': {
      color: `${safeColor === 'error' ? 'var(--mui-palette-error-main)' : 'var(--mui-palette-text-main)'}`,
      borderColor: `var(--mui-palette-${safeColor}-main)`,
      borderWidth: '1px',
      '&:hover': {
        borderColor: `var(--mui-palette-${safeColor}-dark)`,
      },
      '& .MuiTouchRipple-root': {
        color: `var(--mui-palette-${safeColor}-main)`,
      },
    },
  };

  return {
    ...outlinedBorderColor,
    '&[data-chip="true"]': {
      backgroundColor: 'transparent',
      height: '36px',
      borderRadius: '100px',
      border: '1px solid var(--mui-palette-border-main)',
      color: 'var(--mui-palette-text-main)',
      fontSize: '0.875rem',
      fontWeight: 400,
    },
  };
});
